import React from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  Presentation,
  Image as ImageIcon,
  FileDown,
  ExternalLink,
} from "lucide-react";
import { callConfig } from "../data/mockData";

const callTypes = {
  abstract: {
    title: "Call for Abstract",
    icon: <FileText size={48} />,
    description:
      "Submit your abstract for consideration in the conference proceedings.",
    guidelines: [
      "A maximum of four (4) authors (excluding Corrsponding Author) is permitted for a single paper.",
      "The sequence of authors entered in the Google Form should  correct.",
      "Once the abstract is accepted, no changes (addition, deletion, or reordering) in author names will be allowed.",
      "All authors’ full names, official email IDs, and contact details should be filled exactly in the same order as mentioned in the manuscript.",
      "The paper title and abstract must clearly align with at least one theme of the International Conference on Transportation Geotechnics and Sustainable Materials for Advanced Resilient Infrastructure (TG-SMART 2026).",
      "Submissions not matching the conference themes will not be accepted.",
      "The word limitation of abstract is maximum 350 words.",
      "A minimum of three (3) and a maximum of five (5) keywords is acceptable.",
      "Keywords should be relevant to the conference themes and the content of the abstract.",
      "Authors must provide details of two (2) potential reviewers who can review the abstract as well as the full-length paper in later stages.",
      "For each reviewer, the following details are mandatory: Full Name, Affiliation, Official Email ID, and Contact Number",
      "Suggested reviewers must Hold a Ph.D. degree, Be permanent faculty members, Be affiliated with IITs, NITs, Central Universities, State Government Universities, or National Research Institutes, Have research expertise directly relevant to the submitted abstract and must not from same institutions.",
      "The organizing committee reserves the right to verify reviewer credentials.",
      "Any discrepancy, incomplete information, or mismatch in reviewer details will result in rejection of the submission without further correspondence.",
      " It is the sole responsibility of the authors to ensure the accuracy, authenticity, and completeness of all information provided in the Google Form.",
      "Submission of false or misleading information may lead to immediate rejection of the paper at any stage.",
      "Submission of the abstract through the Google Form implies that the authors agree to comply with all rules, timelines, and decisions of the TG-SMART 2026 organizing and technical committees.",
      "The submitted abstract  will be original and free from plagiarism.",
      "Similarity index must be within permissible limits as per standard academic practice (must not exceed 15%).",
      "Authors are fully responsible for the technical accuracy, originality, and ethical compliance of the content.",
      " Manuscripts with excessive AI-generated content beyond the permissible limit will be rejected without review.",
    ],
  },
  papers: {
    title: "Call for Full Papers",
    icon: <FileText size={48} />,
    description:
      "Submit your full research paper following our conference template.",
    guidelines: [
      "Papers should be 6-8 pages including references",
      "Follow the conference paper template strictly",
      "Include abstract, keywords, and references",
      "Original research not published elsewhere",
      "Undergo peer review process",
    ],
  },
  oral: {
    title: "Call for Oral Presentations",
    icon: <Presentation size={48} />,
    description: "Present your research findings to an audience of experts.",
    guidelines: [
      "Presentation duration: 15-20 minutes",
      "Followed by 5 minutes Q&A session",
      "Prepare slides in PowerPoint or PDF",
      "Submit abstract and presentation outline",
      "Selected presenters will be notified by email",
    ],
  },
  poster: {
    title: "Call for Poster Presentations",
    icon: <ImageIcon size={48} />,
    description:
      "Showcase your research through an interactive poster session.",
    guidelines: [
      "Poster size: A0 (841mm × 1189mm) portrait orientation",
      "Include title, authors, methodology, and conclusions",
      "Use clear visuals and minimal text",
      "Author should be present during poster session",
      "Best poster awards will be given",
    ],
  },
};

const Call = () => {
  const { type } = useParams();

  const callType = type && type in callTypes ? type : "abstract";
  const currentCall = callTypes[callType];
  const config = callConfig[callType];

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-blue-950 py-16 text-center overflow-hidden relative">
        <h1 className="relative text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 tracking-wider px-4">
          {currentCall.title.toUpperCase()}
        </h1>
        <div className="relative w-32 sm:w-48 h-1 bg-blue-600 mx-auto"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl shadow-2xl p-8 mb-12 border-t-4 border-blue-600">
          <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
            <div className="text-blue-600">{currentCall.icon}</div>
            <div className="text-center sm:text-left">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {currentCall.title}
              </h2>
              <p className="text-lg text-gray-600">{currentCall.description}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={config.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg text-lg transition-all hover:scale-105 shadow-lg"
            >
              <FileDown size={20} />
              Know Details (PDF)
            </a>
            <a
              href={config.googleFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg"
            >
              <ExternalLink size={20} />
              Submit Now
            </a>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-t-4 border-blue-600">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Important Dates
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { label: "Abstract Submission Deadline", date: "May 30, 2026" },
              { label: "Acceptance Notification", date: "June 20, 2026" },
              { label: "Full Length Paper Submission", date: "Aug 15, 2026" },
              {
                label: "Camera Ready Submission Deadline",
                date: "Sept 30, 2026",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-gradient-to-br from-blue-100 to-blue-200 p-6 rounded-lg border-l-4 border-blue-600"
              >
                <h4 className="font-bold text-gray-800 mb-2 text-lg">
                  {item.label}
                </h4>
                <p className="text-blue-800 font-bold text-xl">{item.date}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl p-8 mb-12 border-l-4 border-blue-600">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Submission Guidelines
          </h2>
          <ul className="space-y-3">
            {currentCall.guidelines.map((guideline, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <span className="text-lg text-gray-700 pt-0.5">
                  {guideline}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="bg-green-50 border-l-4 border-green-600 rounded-xl p-8 mb-4 text-center">
            <h3 className="text-3xl font-bold text-green-900 mb-4">
              Reviewing Process
            </h3>

            <p className="text-lg text-green-800 mb-6">
              The Microsoft CMT service was used for managing the peer-reviewing
              process for this conference. This service was provided for free by
              Microsoft and they bore all expenses, including costs for Azure
              cloud services as well as for software development and support.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Call;
